import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assets, imageByPath, type ImageAsset } from '@/assets';
import { sayAlex, stopAlex } from '@/audio/alex';
import { navigate } from '@/app/router';
import {
  DECLINED,
  NOTES,
  REVEAL,
  SCOPE_NAME,
  SECOND_ROUND,
  TRIES,
  stampLines,
  type LabPose,
  type Note,
} from '@/content/lab';
import { LAB_START } from '@/content/menus';
import { commandCenter } from '@/content/scenes';
import { stripTags } from '@/content/story';
import type { Activity } from '@/features/tongue-lab/lessons';
import { TongueLab } from '@/features/tongue-lab/TongueLab';
import { useAgent } from '@/game/agentProfile';
import { labStore, nextNote, useLab } from '@/game/labProgress';
import { ageBand, useSettings } from '@/game/settings';
import { cx } from '@/lib/cx';
import { YES_NO, type Menu } from '@/speech/control/decide';
import { useVoiceMenu } from '@/speech/control/useVoiceMenu';
import { GameStage } from '@/ui/GameStage';
import { MicButton } from '@/ui/MicButton';
import { TopBar } from '@/ui/TopBar';
import styles from './AlexLab.module.css';
import { ScopeView } from './ScopeView';

/** The lab through a lens focused on the projector's floor (ml/art/bokeh.py). */
const lensPortrait = imageByPath['backgrounds/lab-portrait-bokeh'];
const lensLandscape = imageByPath['backgrounds/lab-landscape-bokeh'];
const lensBackgrounds =
  lensPortrait && lensLandscape ? { portrait: lensPortrait, landscape: lensLandscape } : undefined;

const backgrounds = {
  portrait: imageByPath['backgrounds/lab-portrait'] ?? commandCenter.portrait.background,
  landscape: imageByPath['backgrounds/lab-landscape'] ?? commandCenter.landscape.background,
};

const alex = assets.characters.agentAlex;
/** Alex's lab poses (alex-lab.md); the intro's expressions stand in if one is missing. */
const POSE_ART: Readonly<Record<LabPose, ImageAsset>> = {
  confide: imageByPath['characters/agent-alex/confide'] ?? alex.whisper,
  try: imageByPath['characters/agent-alex/try'] ?? alex.rrr,
  sheepish: imageByPath['characters/agent-alex/sheepish'] ?? alex.worried,
  present: alex.present,
  notebook: imageByPath['characters/agent-alex/notebook'] ?? alex.present,
  hopeful: imageByPath['characters/agent-alex/hopeful'] ?? alex.wave,
};

const PAUSE_MS = 550;

type Phase =
  | { kind: 'reveal'; line: number }
  | { kind: 'ask' }
  | { kind: 'open'; note: Note }
  | { kind: 'activity'; note: Note }
  | { kind: 'stamp'; note: Note };

/**
 * Alex's Lab (docs/design/alex-lab.md): the first time, Alex's secret (his r doesn't count with
 * the masks) and the scope with the 3D mouth; then every visit opens the next page of Alex's
 * notebook, the 3D mouth's activity checks it, the page gets the child's stamp, and Alex tries
 * the r himself. Every question can be answered by voice or tap.
 */
export function AlexLab() {
  const { age } = useSettings();
  const band = ageBand(age);
  const lab = useLab();
  const agent = useAgent();
  const [phase, setPhase] = useState<Phase>(() =>
    lab.reveal === 'accepted' ? { kind: 'open', note: nextNote(lab) } : { kind: 'reveal', line: 0 },
  );
  /** Alex's latest line from an event (a stamp, a try, an answer); the phase's own line otherwise. */
  const [spoken, setSpoken] = useState<{ text: string; pose: LabPose } | null>(null);
  const leaving = useRef(false);

  /** Says a line in response to an event, showing it as the caption. */
  const say = useCallback(async (text: string, as: LabPose = 'present') => {
    setSpoken({ text, pose: as });
    await sayAlex(text);
  }, []);

  const leave = useCallback(() => {
    leaving.current = true;
    stopAlex();
    navigate({ page: 'elevator' });
  }, []);

  // The reveal: one line after another, the scope turning on when Alex shows it.
  const revealLine = phase.kind === 'reveal' ? phase.line : -1;
  useEffect(() => {
    if (revealLine < 0) return;
    const line = REVEAL[band][revealLine];
    if (!line) return;
    let cancelled = false;
    void sayAlex(line.text).then(() => {
      if (cancelled) return;
      setTimeout(() => {
        if (cancelled) return;
        const last = revealLine >= REVEAL[band].length - 1;
        setPhase(last ? { kind: 'ask' } : { kind: 'reveal', line: revealLine + 1 });
      }, PAUSE_MS);
    });
    return () => {
      cancelled = true;
    };
  }, [revealLine, band]);

  // Opening a notebook page.
  const opening = phase.kind === 'open' ? phase.note : null;
  useEffect(() => {
    if (!opening) return;
    const second = lab.round > 1 && lab.checked.length === 0;
    void (async () => {
      if (second) await sayAlex(SECOND_ROUND[band]);
      await sayAlex(lab.round > 1 ? opening.again[band] : opening.opening[band]);
    })();
    // Speak once per page opened, not on every progress change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opening, band]);

  const answerReveal = useCallback(
    (yes: boolean) => {
      labStore.answerReveal(yes);
      if (yes) {
        setSpoken(null);
        setPhase({ kind: 'open', note: nextNote(labStore.get()) });
        return;
      }
      void say(DECLINED[band], 'hopeful').then(() => {
        if (!leaving.current) leave();
      });
    },
    [band, say, leave],
  );

  const finished = useCallback(
    (activity: Activity) => {
      const note = NOTES.find((n) => n.activity === activity);
      if (!note) return;
      labStore.stamp(activity);
      setPhase({ kind: 'stamp', note });
      const tries = TRIES[band];
      const tryLine = tries[labStore.get().tries % tries.length] ?? '';
      labStore.tried();
      void (async () => {
        const [stamped, by] = stampLines(note, band, agent.codename);
        await say(stamped, 'notebook');
        await say(by, 'notebook');
        await say(tryLine, 'try');
        setPhase((p) => (p.kind === 'stamp' ? { kind: 'activity', note } : p));
      })();
    },
    [band, say, agent.codename],
  );

  const menu: Menu | null = useMemo(() => {
    if (phase.kind === 'ask')
      return { ...YES_NO, question: stripTags(REVEAL[band].at(-1)?.text ?? '') };
    if (phase.kind === 'open') return LAB_START;
    return null;
  }, [phase.kind, band]);

  const pick = useCallback(
    (id: string) => {
      if (phase.kind === 'ask') answerReveal(id === 'yes');
      else if (phase.kind === 'open') {
        if (id === 'go') setPhase({ kind: 'activity', note: phase.note });
        else leave();
      }
    },
    [phase, answerReveal, leave],
  );
  const voice = useVoiceMenu(menu, pick, (line) => {
    void say(line);
  });
  const micOn = voice.state.kind !== 'off' && voice.state.kind !== 'error';

  useEffect(
    () => () => {
      stopAlex();
    },
    [],
  );

  const revealing =
    phase.kind === 'reveal'
      ? REVEAL[band][phase.line]
      : phase.kind === 'ask' && !spoken
        ? REVEAL[band].at(-1)
        : undefined;
  const current = revealing
    ? { text: revealing.text, pose: revealing.pose }
    : phase.kind === 'open' && !spoken
      ? {
          text: lab.round > 1 ? phase.note.again[band] : phase.note.opening[band],
          pose: 'present' as const,
        }
      : (spoken ?? { text: '', pose: 'present' as const });
  const caption = stripTags(current.text);
  const pose = current.pose;
  // The scope is lit from the reveal line where Alex shows it, and stays lit after.
  const scopeOn =
    phase.kind !== 'reveal' || REVEAL[band].slice(0, phase.line + 1).some((l) => l.scope);
  const inActivity = phase.kind === 'activity' || phase.kind === 'stamp';
  const note = 'note' in phase ? phase.note : null;
  const stamped = NOTES.filter((n) => lab.checked.includes(n.activity)).length;

  return (
    // The room goes out of focus while the hologram is on (GameStage focus).
    <GameStage
      backgrounds={backgrounds}
      focus={scopeOn || inActivity}
      focusBackgrounds={lensBackgrounds}
    >
      <div className={styles.screen}>
        <TopBar onBack={leave} onSettings={leave}>
          <span className={styles.title}>
            Alex’s Lab
            {lab.reveal === 'accepted' && (
              <span className={styles.count}>
                {' '}
                · notebook {stamped} of {NOTES.length}
              </span>
            )}
          </span>
        </TopBar>

        {inActivity && note ? (
          <div className={styles.activity}>
            <TongueLab
              holo
              activity={note.activity}
              onDone={(a) => {
                finished(a);
              }}
              onStart={(a) => {
                const next = NOTES.find((n) => n.activity === a);
                if (next)
                  void say(lab.round > 1 ? next.again[band] : next.opening[band], 'present');
              }}
            />
            {phase.kind === 'stamp' && (
              // Alex stamps the page and then tries the r himself: he steps in for that beat.
              <img
                className={cx(styles.alexCorner, pose === 'try' && styles.trying)}
                src={POSE_ART[pose].url}
                alt="Agent Alex"
              />
            )}
            {phase.kind === 'stamp' && (
              <div className={styles.stampCard} role="status">
                <span className={styles.stamp}>Checked</span>
                <strong>{note.title}</strong>
                <span>{agent.codename ? `Agent ${agent.codename}` : 'by you'}</span>
              </div>
            )}
            <p className={styles.captionBar} aria-live="polite">
              {caption}
            </p>
          </div>
        ) : (
          <div className={styles.room}>
            <img
              className={cx(styles.alex, pose === 'try' && styles.trying)}
              src={POSE_ART[pose].url}
              alt="Agent Alex"
            />
            <ScopeView on={scopeOn} label={`The ${SCOPE_NAME[band]}: a mouth seen from inside`} />
            <div className={styles.talk}>
              {phase.kind === 'open' && (
                <div className={styles.page}>
                  <span className={styles.pageLabel}>
                    Notebook page {NOTES.indexOf(phase.note) + 1}
                  </span>
                  <strong className={styles.pageTitle}>{phase.note.title}</strong>
                </div>
              )}
              <p className={styles.bubble} aria-live="polite">
                {caption}
              </p>
              {phase.kind === 'reveal' && (
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => {
                    stopAlex();
                    setPhase({ kind: 'ask' });
                  }}
                >
                  Skip
                </button>
              )}
              {phase.kind === 'ask' && (
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.primary}
                    onClick={() => {
                      answerReveal(true);
                    }}
                  >
                    Yes!
                  </button>
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => {
                      answerReveal(false);
                    }}
                  >
                    Not now
                  </button>
                </div>
              )}
              {phase.kind === 'open' && (
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.primary}
                    onClick={() => {
                      pick('go');
                    }}
                  >
                    Check it!
                  </button>
                  <button type="button" className={styles.secondary} onClick={leave}>
                    Back to the elevator
                  </button>
                </div>
              )}
              {menu && (
                <div className={styles.micRow}>
                  <MicButton
                    state={
                      voice.state.kind === 'listening'
                        ? 'listening'
                        : voice.state.kind === 'loading' || voice.state.kind === 'thinking'
                          ? 'busy'
                          : 'idle'
                    }
                    label={micOn ? 'Stop listening' : 'Answer by voice'}
                    size={76}
                    pressed={micOn}
                    onPress={() => {
                      if (micOn) void voice.stop();
                      else void voice.start();
                    }}
                  />
                  <span className={styles.heard}>
                    {voice.heard
                      ? `I heard: “${voice.heard}”`
                      : micOn
                        ? 'Say it!'
                        : 'Tap to answer by voice'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </GameStage>
  );
}
